<template>
	<v-container fluid class="dashboard-product-view">
		<v-card class="mb-4">
			<v-toolbar color="primary" density="comfortable">
				<v-toolbar-title class="text-white">
					Produits ({{ products.length }})
				</v-toolbar-title>
			</v-toolbar>
			<!-- Filter fields -->
			<v-card-text>
				<v-row dense>
					<v-col cols="12" md="2">
						<v-autocomplete density="compact" v-model="filters.productMake" :items="productMakes"
							label="Fabricant" clearable />
					</v-col>

					<v-col cols="12" md="2">
						<v-autocomplete density="compact" v-model="filters.productType" :items="productTypes"
							label="Type de Produit" clearable />
					</v-col>

					<v-col cols="12" md="2">
						<v-autocomplete density="compact" v-model="filters.productModelId" :items="productModels" item-value="id" item-title="model" label="Modèle"
							clearable />
					</v-col>

					<v-col cols="12" md="2">
						<v-autocomplete density="compact" v-model="filters.productStatus" :items="productStatuses"
							label="Statut du Produit" clearable />
					</v-col>

					<v-col cols="12" md="2">
						<v-autocomplete density="compact" v-model="filters.reservationStatus"
							:items="reservationStatuses" label="Statut de Réservation" clearable />
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="12" md="2">
						<v-text-field density="compact" v-model="filters.destructionDateFrom" type="date"
							label="Destruction du" clearable />
					</v-col>

					<v-col cols="12" md="2">
						<v-text-field density="compact" v-model="filters.destructionDateTo" type="date"
							label="Destruction au" clearable :min="filters.destructionDateFrom ?? undefined" />
					</v-col>

					<v-col cols="12" md="2">
						<v-text-field density="compact" v-model="filters.bonEntreeDateFrom" type="date"
							label="Bon d'entrée du" clearable />
					</v-col>

					<v-col cols="12" md="2">
						<v-text-field density="compact" v-model="filters.bonEntreeDateTo" type="date"
							label="Bon d'entrée au" clearable :min="filters.bonEntreeDateFrom ?? undefined" />
					</v-col>
				</v-row>

				<div class="d-flex ga-2 mt-2">
					<v-btn color="primary" :loading="loading" @click="searchProducts">
						Rechercher
					</v-btn>
					<v-btn variant="outlined" :disabled="loading" @click="resetFilters">
						Reset
					</v-btn>
				</div>
			</v-card-text>
		</v-card>

		<v-card class="results-card">
			<v-card-title class="d-flex align-center justify-space-between">
				<span>Résultats des Produits</span>
				<span class="text-body-2 text-medium-emphasis">{{ products.length }} résultat(s)</span>
			</v-card-title>

			<v-alert v-if="errorMessage" class="mx-4 mb-2" type="error" variant="tonal" density="comfortable">
				{{ errorMessage }}
			</v-alert>

			<v-card-text class="results-scroll">
				<v-expansion-panels v-if="products.length > 0" v-model="expandedProductId">
					<v-expansion-panel v-for="item in products" :key="item.id" :value="item.id">
						<v-expansion-panel-title>
							<div>
								<div class="font-weight-medium">Aqua N°: {{ item.aquaNb }}</div>
								<div class="text-body-2 text-medium-emphasis">N° Série: {{ item.serialNb }}</div>
								<div v-if="item.destructionDate" class="text-body-2 text-medium-emphasis">
									Date de destruction: {{ formatDestructionDate(item.destructionDate) }}
								</div>
							</div>
						</v-expansion-panel-title>
						<v-expansion-panel-text>
							<ProductEditor
								:key="item.id"
								:product-id="String(item.id)"
								embedded
								@close="expandedProductId = null"
								@saved="updateProductInResults"
							/>
						</v-expansion-panel-text>
					</v-expansion-panel>
				</v-expansion-panels>

				<div v-else-if="!loading" class="text-medium-emphasis px-4 py-2">
					Aucun produit à afficher.
				</div>
			</v-card-text>
		</v-card>
	</v-container>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useAppStore } from "@/store/app";
import { getFilteredProducts } from "@/utils/product-api";
import { fetchFilteredProductModels } from "@/utils/product-model-api";
import ProductEditor from "@/pages/ProductEditor.vue";
import {
	ProductMake,
	ProductStatus,
	ProductType,
	ReservationStatus,
	type Product,
} from "@/types";
import type { ProductModel } from "@/types/product-model";

type ProductFilters = {
	productMake: ProductMake | null;
	productType: ProductType | null;
	productModelId: string | null;
	productStatus: ProductStatus | null;
	reservationStatus: ReservationStatus | null;
	destructionDateFrom: string | null;
	destructionDateTo: string | null;
	bonEntreeDateFrom: string | null;
	bonEntreeDateTo: string | null;
};

const PRODUCT_SEARCH_FILTERS_KEY = "productSearchFilters";

const appStore = useAppStore();

const loading = ref(false);
const errorMessage = ref("");
const products = ref<Product[]>([]);
const expandedProductId = ref<Product["id"] | null>(null);

const filters = ref<ProductFilters>({
	productMake: null,
	productType: null,
	productModelId: null,
	productStatus: null,
	reservationStatus: null,
	destructionDateFrom: null,
	destructionDateTo: null,
	bonEntreeDateFrom: null,
	bonEntreeDateTo: null,
});

const reservationStatuses = Object.values(ReservationStatus);
const productStatuses = Object.values(ProductStatus);
const productTypes = Object.values(ProductType);
const productMakes = Object.values(ProductMake);

const productModels = ref<ProductModel[]>([]);

const getStatusesForQuery = (): ReservationStatus[] => {
	if (filters.value.reservationStatus) {
		return [filters.value.reservationStatus];
	}
	return reservationStatuses;
};

const hasAnyFilterValue = (inputFilters: ProductFilters): boolean => {
	return Boolean(
		inputFilters.productMake ||
		inputFilters.productType ||
		inputFilters.productModelId ||
		inputFilters.productStatus ||
		inputFilters.reservationStatus ||
		inputFilters.destructionDateFrom ||
		inputFilters.destructionDateTo ||
		inputFilters.bonEntreeDateFrom ||
		inputFilters.bonEntreeDateTo
	);
};

const createEmptyFilters = (): ProductFilters => ({
	productMake: null,
	productType: null,
	productModelId: null,
	productStatus: null,
	reservationStatus: null,
	destructionDateFrom: null,
	destructionDateTo: null,
	bonEntreeDateFrom: null,
	bonEntreeDateTo: null,
});

const saveFiltersToLocalStorage = () => {
	localStorage.setItem(PRODUCT_SEARCH_FILTERS_KEY, JSON.stringify(filters.value));
};

const loadFiltersFromLocalStorage = (): ProductFilters | null => {
	const rawFilters = localStorage.getItem(PRODUCT_SEARCH_FILTERS_KEY);
	if (!rawFilters) {
		return null;
	}

	try {
		const parsed = JSON.parse(rawFilters) as Partial<ProductFilters>;
		return {
			productMake: productMakes.includes(parsed.productMake as ProductMake)
				? (parsed.productMake as ProductMake)
				: null,
			productType: productTypes.includes(parsed.productType as ProductType)
				? (parsed.productType as ProductType)
				: null,
			productModelId: typeof parsed.productModelId === "string" && parsed.productModelId.trim().length > 0
				? parsed.productModelId
				: null,
			productStatus: productStatuses.includes(parsed.productStatus as ProductStatus)
				? (parsed.productStatus as ProductStatus)
				: null,
			reservationStatus: reservationStatuses.includes(parsed.reservationStatus as ReservationStatus)
				? (parsed.reservationStatus as ReservationStatus)
				: null,
			destructionDateFrom:
				typeof parsed.destructionDateFrom === "string" && parsed.destructionDateFrom.trim().length > 0
					? parsed.destructionDateFrom
					: null,
			destructionDateTo:
				typeof parsed.destructionDateTo === "string" && parsed.destructionDateTo.trim().length > 0
					? parsed.destructionDateTo
					: null,
			bonEntreeDateFrom:
				typeof parsed.bonEntreeDateFrom === "string" && parsed.bonEntreeDateFrom.trim().length > 0
					? parsed.bonEntreeDateFrom
					: null,
			bonEntreeDateTo:
				typeof parsed.bonEntreeDateTo === "string" && parsed.bonEntreeDateTo.trim().length > 0
					? parsed.bonEntreeDateTo
					: null,
		};
	} catch {
		return null;
	}
};

const fetchProducts = async () => {
	try {
		loading.value = true;
		errorMessage.value = "";

		const serverProducts = await getFilteredProducts(
			getStatusesForQuery(),
			filters.value.productMake ?? undefined,
			filters.value.productType ?? undefined,
			filters.value.productStatus ?? undefined,
			filters.value.productModelId ?? undefined,
			filters.value.destructionDateFrom ?? undefined,
			filters.value.destructionDateTo ?? undefined,
			filters.value.bonEntreeDateFrom ?? undefined,
			filters.value.bonEntreeDateTo ?? undefined
		);
		products.value = serverProducts;
	} catch (e) {
		errorMessage.value = "Failed to fetch products.";
		appStore.sendErrorNotice("Failed to fetch products.");
	} finally {
		loading.value = false;
	}
};

const loadInitialData = async () => {
	const savedFilters = loadFiltersFromLocalStorage();
	if (!savedFilters || !hasAnyFilterValue(savedFilters)) {
		products.value = [];
		return;
	}

	filters.value = savedFilters;
	await fetchProducts();
};

const keepDateRangeValid = (
	fromAccessor: () => string | null,
	toAccessor: () => string | null,
	clearTo: () => void
) => {
	const fromDate = fromAccessor();
	const toDate = toAccessor();
	if (fromDate && toDate && toDate < fromDate) {
		clearTo();
	}
};

watch(
	() => filters.value.productMake,
	async (productMake) => {
		filters.value.productModelId = null;

		if (!productMake) {
			productModels.value = [];
			return;
		}

		try {
			productModels.value = await fetchFilteredProductModels(productMake);
		} catch (e) {
			productModels.value = [];
		}
	},
	{ immediate: true }
);

watch(
	() => filters.value.destructionDateFrom,
	() => {
		keepDateRangeValid(
			() => filters.value.destructionDateFrom,
			() => filters.value.destructionDateTo,
			() => {
				filters.value.destructionDateTo = null;
			}
		);
	}
);

watch(
	() => filters.value.bonEntreeDateFrom,
	() => {
		keepDateRangeValid(
			() => filters.value.bonEntreeDateFrom,
			() => filters.value.bonEntreeDateTo,
			() => {
				filters.value.bonEntreeDateTo = null;
			}
		);
	}
);

const searchProducts = async () => {
	saveFiltersToLocalStorage();
	await fetchProducts();
};

const resetFilters = async () => {
	filters.value = createEmptyFilters();
	localStorage.removeItem(PRODUCT_SEARCH_FILTERS_KEY);
	products.value = [];
};

const updateProductInResults = (updatedProduct: Product) => {
	products.value = products.value.map((item) =>
		item.id === updatedProduct.id ? updatedProduct : item
	);
};

const formatDestructionDate = (value: Product["destructionDate"] | string): string => {
	if (!value) {
		return "";
	}

	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) {
		return String(value);
	}

	return date.toLocaleDateString("fr-FR");
};

onMounted(async () => {
	await loadInitialData();
});
</script>

<style scoped>
.dashboard-product-view {
	padding-bottom: 24px;
}

.results-card {
	display: block;
}

.results-scroll {
	max-height: min(45vh, 420px);
	overflow-y: auto;
}
</style>
